
import React, { useState } from "react";
import { Box, Typography, Avatar, Paper, Divider, Button, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from "@mui/material";
import { db, storage } from "../utils/firebase";
import { doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useAuth } from "../context/AuthContext";

export default function AdminUserProfile({ user, onClose, onDeleted }) {
  const { user: currentUser } = useAuth();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  if (!user) return null;
  return (
    <Paper sx={{ p: 3, mb: 3, maxWidth: 400 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Avatar src={user.profilePicture} sx={{ width: 64, height: 64, mr: 2 }} />
        <Box>
          <Typography variant="h6">{user.name || user.email}</Typography>
          <Typography variant="body2" color="text.secondary">{user.email}</Typography>
          <Button
            variant="outlined"
            size="small"
            component="label"
            sx={{ mt: 1 }}
          >
            Change Profile Picture
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={async e => {
                if (e.target.files && e.target.files[0]) {
                  const file = e.target.files[0];
                  try {
                    // Upload to Firebase Storage
                    const storageRef = ref(storage, `profilePictures/${user.uid}_${Date.now()}_${file.name}`);
                    await uploadBytes(storageRef, file);
                    const url = await getDownloadURL(storageRef);
                    // Update Firestore user doc
                    await updateDoc(doc(db, "users", user.uid), {
                      pendingProfilePicture: url,
                      profilePictureApproved: false
                    });
                    alert("Profile picture uploaded! Pending admin approval.");
                  } catch (err) {
                    alert("Failed to upload profile picture: " + err.message);
                  }
                }
              }}
            />
          </Button>
        </Box>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="body2"><b>Gender:</b> {user.gender || 'N/A'}</Typography>
      <Typography variant="body2"><b>Phone:</b> {user.phone || 'N/A'}</Typography>
      <Typography variant="body2"><b>City:</b> {user.city || 'N/A'}</Typography>
      <Typography variant="body2"><b>State:</b> {user.state || 'N/A'}</Typography>
      <Typography variant="body2"><b>UID:</b> {user.uid}</Typography>
      {onClose && (
        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" onClick={onClose}>Back to User List</Button>
          <Button
            variant="contained"
            color="error"
            sx={{ ml: 1 }}
            onClick={() => setConfirmOpen(true)}
          >
            Delete User
          </Button>
        </Box>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will permanently delete this user and their messages and notifications. This cannot be undone.
          </Alert>
          <Typography>Are you sure you want to delete {user.name || user.email || user.uid}?</Typography>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={deleting}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleting}
            onClick={async () => {
              setError('');
              setDeleting(true);
              try {
                if (!currentUser) throw new Error('Not authenticated');
                const token = await currentUser.getIdToken();
                const backendUrl = (process.env.REACT_APP_BACKEND_URL || '');
                const resp = await fetch(`${backendUrl}/api/users/${user.uid}`, {
                  method: 'DELETE',
                  headers: { Authorization: `Bearer ${token}` }
                });
                const json = await resp.json().catch(() => ({}));
                if (!resp.ok) throw new Error(json?.message || `HTTP ${resp.status}`);
                setConfirmOpen(false);
                if (onDeleted) onDeleted();
                else if (onClose) onClose();
              } catch (e) {
                setError(e.message || 'Failed to delete user');
              } finally {
                setDeleting(false);
              }
            }}
          >
            {deleting ? 'Deleting…' : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
